import { defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';

import { Modal, ModalTransition } from '@atlaskit/onboarding';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { useRovoDevAgentsRequirements } from 'src/components/rovo-dev-agents/src/hooks';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useIntl } from 'src/hooks/intl';
import { usePref } from 'src/hooks/use-pref';
import {
  getCurrentRepositoryFullSlug,
  getRepositoryAccessLevel,
} from 'src/selectors/repository-selectors';

import aiCodeReviewerOnboardingImage from './code-reviewer-repo-onboarding-img.png';

const messages = defineMessages({
  aiCodeReviewerOnboardingHeader: {
    id: 'frontbucket.repository.aiCodeReviewerOnboarding.header',
    description: 'Header for AI Code Reviewer onboarding',
    defaultMessage: 'Get started with Code Reviewer',
  },
  aiCodeReviewerOnboardingBody: {
    id: 'frontbucket.repository.aiCodeReviewerOnboarding.body',
    description: 'Body for AI Code Reviewer onboarding',
    defaultMessage:
      'Stuff about this new thing and how it can review pull requests. Great for your teams productivity. Take it for a spin and let us know what you think.',
  },
  takeMeThere: {
    id: 'frontbucket.repository.aiCodeReviewerOnboarding.button.takeMeThere',
    description:
      'Text to display on the button to navigate to the AI Code Reviewer page',
    defaultMessage: 'Take me there?',
  },
  dismiss: {
    id: 'frontbucket.repository.aiCodeReviewerOnboarding.button.dismiss',
    description: 'Text to display on the button to dismiss the onboarding',
    defaultMessage: 'Dismiss',
  },
});

export const DevAIRepoAdminsOnboarding = (): JSX.Element | null => {
  const intl = useIntl();
  const isCodeReviewerOnboardingEnabled = useStatsigGate(
    StatsigFeatureKeys.aiCodeReviewOnboardingFlag
  );
  const repositoryAccessLevel = useSelector(getRepositoryAccessLevel);
  const isRepoAdmin = repositoryAccessLevel === 'admin';
  const [codeReviewerRepoOnboardingSeen, updatePref, isLoading] = usePref(
    'ai-code-reviewer-repo-onboarding-seen'
  );
  const { hasConnectedJiraSite, hasEnabledRovoDevAgents } =
    useRovoDevAgentsRequirements();
  const repoSlug = useSelector(getCurrentRepositoryFullSlug);
  const shouldNotShowModal =
    !isCodeReviewerOnboardingEnabled ||
    codeReviewerRepoOnboardingSeen ||
    isLoading ||
    !isRepoAdmin ||
    (!hasEnabledRovoDevAgents && !hasConnectedJiraSite);

  if (shouldNotShowModal) {
    return null;
  }

  const handleRedirectToSettings = () => {
    updatePref('true');
    window.location.href = `/${repoSlug}/admin/code-reviewer/`;
  };

  const handleDismiss = () => {
    updatePref('true');
  };

  const renderModal = () => {
    return (
      <Modal
        experimental_shouldShowPrimaryButtonOnRight
        actions={[
          {
            onClick: handleRedirectToSettings,
            text: intl.formatMessage(messages.takeMeThere),
          },
          {
            onClick: () => handleDismiss(),
            text: intl.formatMessage(messages.dismiss),
          },
        ]}
        heading={intl.formatMessage(messages.aiCodeReviewerOnboardingHeader)}
        image={aiCodeReviewerOnboardingImage}
        key="ai-repo-code-reviewer-onboarding"
      >
        {intl.formatMessage(messages.aiCodeReviewerOnboardingBody)}
      </Modal>
    );
  };

  return <ModalTransition>{renderModal()}</ModalTransition>;
};
